import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/screens/_shared";
import { getTransactions } from "@/lib/monetizationStore";
import { useMoneVersion, timeAgo } from "@/lib/hooks";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

const TransactionsScreen = () => {
    const { user } = useAuth();
    const v = useMoneVersion();
    const txns = useMemo(() => (user ? getTransactions(user.id) : []), [user, v]);

    return (
        <div className="screen" data-testid="transactions-screen">
            <PageHeader title="Transactions" />
            <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-3">
                {txns.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground text-[13px]">
                        No transactions yet. Your earnings and payments will appear here.
                    </div>
                ) : (
                    <ul className="space-y-1.5">
                        {txns.map((t) => {
                            const credit = t.type === "credit";
                            return (
                                <li
                                    key={t.id}
                                    data-testid={`txns-row-${t.id}`}
                                    className="flex items-center gap-3 p-3 rounded-2xl border border-border bg-card"
                                >
                                    <span
                                        className={[
                                            "h-10 w-10 rounded-xl flex items-center justify-center",
                                            credit
                                                ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                                : "bg-destructive/10 text-destructive",
                                        ].join(" ")}
                                    >
                                        {credit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[13.5px] font-medium truncate">{t.title}</div>
                                        <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                                            <span>{timeAgo(t.at)}</span>
                                            {t.reference && (
                                                <>
                                                    <span>·</span>
                                                    <span className="font-mono">{t.reference}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className={[
                                            "font-display text-[13.5px] font-semibold",
                                            credit ? "text-emerald-600 dark:text-emerald-400" : "",
                                        ].join(" ")}
                                    >
                                        {credit ? "+" : "−"}₦{Number(t.amount).toLocaleString()}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default TransactionsScreen;
